import * as React from "react"
import {Box, Chip, Grid, Tooltip, Typography} from "@mui/material";
import {AppLink} from "@ui-seeker/app";
import {CheckboxSeeker, IconSeeker} from "@ui-seeker/core";
import {MatchersLazyImage} from "../../@components";
import {FORMAT_DATE_BACK, formatDate, formatPrice, getCurrentDate} from "@ui-seeker/utils";

import {BACKEND_APIS} from "@constants";
import {imageModalData} from "@signals";
import {useProxiedImage} from "../../@hooks";
import {productsMatcherImageComparatorModalData} from "../@signals";
import {editMappingDrawer} from "../../../AuditLog/@signals";


const currentDateString = formatDate(getCurrentDate(), { format: FORMAT_DATE_BACK })

// Relative "last seen" label (English): today / yesterday / N days ago / N weeks ago / ...
const lastSeenRelative = (dateStr) => {
	if (!dateStr) return '—'
	const d = new Date(dateStr)
	if (isNaN(d.getTime())) return dateStr
	const startOfDay = (x) => { const c = new Date(x); c.setHours(0, 0, 0, 0); return c }
	const days = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86400000)
	if (days <= 0) return 'today'
	if (days === 1) return 'yesterday'
	if (days < 7) return `${days} days ago`
	if (days < 14) return 'last week'
	if (days < 30) return `${Math.floor(days / 7)} weeks ago`
	if (days < 60) return 'last month'
	if (days < 365) return `${Math.floor(days / 30)} months ago`
	return `${Math.floor(days / 365)} year(s) ago`
}

// Función para determinar el color del badge según el score
const getScoreColor = (distance) => {
	const score = parseFloat(distance)
	if (isNaN(score)) return 'default'
	
	if (score >= 0.9) return 'success'      // Verde - Excelente match
	if (score >= 0.7) return 'primary'      // Azul - Buen match
	if (score >= 0.5) return 'warning'      // Naranja - Match medio
	return 'error'                          // Rojo - Match bajo
}

// Memoised: the selection is a signal the whole page reads, so every change re-rendered the
// complete candidate list (hundreds of cards for a big brand). With `isSelected` as a boolean
// prop only the two cards that change re-render. Same fix as the Raw matcher (869fkbgeq).
function ProductOption({ skuID, skuName, subjectImage, productID, isSelected=false, data, setSelectedSKU, brandOptionsIndex=0, isIAOption=false, groupCount=0, groupProductIDs=[] }) {
	const isAlreadyMapped = data.alreadyMapped === true
	// Candidate already belongs to THIS product's own group (e.g. via a legit own duplicate):
	// harmless — validating changes nothing, nothing gets unmapped. Green, not warning.
	const isSameGroup = data.sameGroup === true
	const border = isSelected ? "4px solid" : '1px solid'
	const borderColor = isSameGroup ? "success.main" : (isAlreadyMapped ? "warning.main" : "primary.light")

	const handleCheck = () => setSelectedSKU(isSelected ? null : productID, groupProductIDs)
	const { decodedURL: __thumbnail, imageResourceURL, proxyURL } = useProxiedImage(data.thumbnail)
	const { decodedURL: mappedFull, imageResourceURL: mappedImg, proxyURL: mappedProxy } = useProxiedImage(data.mappedTo?.thumbnail)
	// Fall back to the server proxy only if the direct CDN load fails (rare: http URLs, stubborn CDNs)
	const fallbackToProxy = (proxy) => (e) => {
		if (proxy && !e.target.dataset.fb) { e.target.dataset.fb = '1'; e.target.src = proxy }
	}
	const candidateName = `${data.superTitle || ''} ${data.productTitle} ${data.subtitle || ''} ${data.variant || ''}`.trim()

	return <Grid item xs={12}><Grid container border={border} borderColor={borderColor} borderRadius={1} p={.5} pt={0} cursor="pointer" spacing={2} onClick={handleCheck}>
		{ isSameGroup && <Grid item xs={12}>
			<Box display="flex" alignItems="center" gap={1.5} bgcolor="success.light" borderRadius={1} px={1.5} py={1}>
				<IconSeeker icon="circle-check" sx={{ color: 'success.dark', fontSize: "1.35rem" }} />
				<Box display="flex" flexDirection="column" gap={0.25} minWidth={0}>
					<Typography fontWeight="bold" fontSize="0.8rem" sx={{ color: 'success.dark', textTransform: 'uppercase', letterSpacing: 0.3 }}>
						Already in this product's group
					</Typography>
					<Typography
						fontSize="0.85rem"
						sx={{ color: 'success.dark', cursor: data.mappedTo ? 'pointer' : 'default', '&:hover': data.mappedTo ? { opacity: 0.85 } : undefined }}
						onClick={(e) => {
							if (!data.mappedTo) return
							e.stopPropagation()
							editMappingDrawer.value = {
								raw: {
									product_id: data.mappedTo.productID,
									id_site: data.mappedTo.idSite,
									title: data.mappedTo.title,
									image: data.mappedTo.thumbnail,
									variant_raw: data.mappedTo.variant,
									product_url: data.mappedTo.productURL,
									site: data.mappedTo.siteName,
								},
								idMatch: data.mappedTo.idMatch,
								idCustomer: data.mappedTo.idCustomer,
								mappingType: 'raw2raw',
							}
						}}
					>
						Grouped with {data.mappedTo?.title || 'this product'} — validating changes nothing, nothing gets unmapped{data.mappedTo ? ' · click to view group' : ''}
					</Typography>
				</Box>
			</Box>
		</Grid> }
		{ isAlreadyMapped && !isSameGroup && <Grid item xs={12}>
			<Box
				display="flex"
				alignItems="center"
				gap={1.5}
				bgcolor="warning.light"
				borderRadius={1}
				px={1.5}
				py={1}
			>
				<IconSeeker icon="triangle-exclamation" sx={{ color: 'warning.dark', fontSize: "1.35rem" }} />
				{ data.mappedTo ? (
					<Box display="flex" alignItems="center" justifyContent="space-between" gap={1.5} width="100%">
						{/* Text block (left) — click opens the group drawer */}
						<Box
							display="flex"
							flexDirection="column"
							gap={0.25}
							sx={{ cursor: 'pointer', minWidth: 0, '&:hover': { opacity: 0.85 } }}
							onClick={(e) => {
								e.stopPropagation()
								editMappingDrawer.value = {
									raw: {
										product_id: data.mappedTo.productID,
										id_site: data.mappedTo.idSite,
										title: data.mappedTo.title,
										image: data.mappedTo.thumbnail,
										variant_raw: data.mappedTo.variant,
										product_url: data.mappedTo.productURL,
										site: data.mappedTo.siteName,
									},
									idMatch: data.mappedTo.idMatch,
									idCustomer: data.mappedTo.idCustomer,
									mappingType: 'raw2raw',
								}
							}}
						>
							<Typography fontWeight="bold" fontSize="0.8rem" sx={{ color: 'warning.dark', textTransform: 'uppercase', letterSpacing: 0.3 }}>
								Already matched to
							</Typography>
							<Typography fontSize="1rem" fontWeight="bold" sx={{ color: '#7a4f01' }}>
								{data.mappedTo.title}
								<Typography component="span" fontSize="0.9rem" fontWeight="normal" sx={{ color: '#7a4f01', ml: 0.75 }}>
									· {data.mappedTo.siteName}
								</Typography>
							</Typography>
							<Typography fontSize="0.8rem" sx={{ color: '#7a4f01', fontFamily: 'monospace', opacity: 0.85 }}>
								ID: {data.mappedTo.productID} · click to view group
							</Typography>
							<Typography fontSize="0.8rem" sx={{ color: '#7a4f01' }}>
								Selecting it JOINS this product into the group above (both will share all mappings) — right only if they are the same product / duplicates
							</Typography>
						</Box>
						{/* Photo (right) — click opens the left/right comparison */}
						{ data.mappedTo.thumbnail && <MatchersLazyImage
							useSpinner
							src={mappedImg}
							onError={fallbackToProxy(mappedProxy)}
							sx={{ width: 64, height: 'auto', maxHeight: 72, borderRadius: 0.5, border: '1px solid', borderColor: 'warning.dark', cursor: 'zoom-in', bgcolor: 'background.paper', flexShrink: 0 }}
							onClick={(e) => {
								e.stopPropagation()
								productsMatcherImageComparatorModalData.value = {
									title: data.mappedTo.title,
									urlLeft: subjectImage,
									urlRight: mappedFull,
									skuName: skuName,
									candidateName: data.mappedTo.title,
									targetIndex: brandOptionsIndex,
								}
							}}
						/> }
					</Box>
				) : (
					<Typography fontWeight="bold" fontSize="0.95rem" sx={{ color: '#7a4f01' }}>
						Already in a match group — selecting it will add it to this match (no unmap)
					</Typography>
				) }
			</Box>
		</Grid> }
		
		<Grid item xs={2} display="flex" justifyContent="center">
			<CheckboxSeeker label="" checked={isSelected} onChange={handleCheck} sx={{ mx: 0 }} />

			<Box position="relative">
				<MatchersLazyImage
					useSpinner
					src={imageResourceURL}
					onError={fallbackToProxy(proxyURL)}
					sx={{ border: '1px solid', borderColor: 'divider', width: 150, height: 'auto', maxHeight: 200, cursor: 'pointer', borderRadius: 1 }}
					onClick={() => productsMatcherImageComparatorModalData.value = {
						title: `${data.superTitle || ''} ${data.productTitle} ${data.subtitle || ''}`,
						urlLeft: subjectImage,
						urlRight: __thumbnail,
						skuName: skuName,
						candidateName: candidateName,
						targetIndex: brandOptionsIndex
					}}
				/>
				{isIAOption && (
					<Box position="absolute" top={-8} right={-8}>
						<Chip
							icon={<IconSeeker icon="sparkles" sx={{ fontSize: "0.7rem !important", color: "inherit !important" }} />}
							label={data.AIDistance ? `${Math.round(parseFloat(data.AIDistance) * 100)}%` : "N/A"}
							color={getScoreColor(data.AIDistance)}
							variant="filled"
							size="small"
							sx={{ fontSize: "0.7rem", height: 20, fontWeight: 'bold' }}
						/>
					</Box>
				)}
				</Box>
		</Grid>

		<Grid item xs={10} display="flex" flexDirection="column" justifyContent="flex-start">
			<Box display="flex" flexDirection="column">
				{ data.superTitle && <Typography color="text.disabled" fontSize="0.95rem">
					{data.superTitle}
				</Typography> }
				<AppLink to={data.productURL || `${BACKEND_APIS.BACKEND}/sku/product-url/${data.siteID}/${productID}/${currentDateString}`} target="_blank">
					<Typography display="flex" alignItems="center" gap={1}>
						{data.productTitle} {data.variant && `${data.variant}`}
						<IconSeeker icon="external-link" sx={{ color: 'text.disabled', fontSize: "0.85rem" }} />
					</Typography>
				</AppLink>
				{ data.subtitle && <Typography color="text.secondary" fontSize="0.95rem">
					{data.subtitle}
				</Typography> }
			</Box>
			<Box display="flex" justifyContent="space-between">
				{groupCount <= 1 && <Typography fontSize="0.85rem">
					ProductID: {data.productID}
				</Typography>}

				<Typography fontSize="0.85rem" sx={groupCount > 1 ? { ml: 'auto' } : undefined}>
					Last Seen: {lastSeenRelative(data.date)}
				</Typography>
			</Box>
			<Box display="flex" justifyContent="space-between" alignItems="center">
				<Typography fontSize="0.85rem">
					Variant: {data.variant}
				</Typography>

				<Typography fontSize="0.85rem">
					Actual Price: {formatPrice(data.actualPrice, data.currency)}
				</Typography>
			</Box>
			{data.reference && <Typography fontSize="0.85rem" color="text.secondary">
				Reference: {data.reference}
			</Typography>}
			{groupCount > 1 && (
				<Box mt={0.5}>
					<Tooltip
						title={
							<Box>
								<Typography fontSize="0.75rem" fontWeight="bold" mb={0.5}>Product IDs in group:</Typography>
								{groupProductIDs.map(id => (
									<Typography key={id} fontSize="0.7rem" fontFamily="monospace">{id}</Typography>
								))}
							</Box>
						}
						arrow
						placement="bottom-start"
					>
						<Chip
							icon={<IconSeeker icon="layer-group" sx={{ fontSize: "0.7rem !important", color: "inherit !important" }} />}
							label={`${groupCount} products`}
							color="info"
							variant="filled"
							size="small"
							sx={{ fontSize: "0.7rem", height: 20, fontWeight: 'bold', cursor: 'help' }}
						/>
					</Tooltip>
				</Box>
			)}
		</Grid>
	</Grid></Grid>
}

export default React.memo(ProductOption)
